<template>
    <div
        v-if="initialized"
        v-bind:id="internalId"
        class="card">
        <div
            v-bind:class="headerClassObject"
            class="card-header px-3 py-2 fw-bold d-flex align-items-center"
            data-test-role="capsule-header"
            v-on:click="toggle">
            <div
                v-if="showCollapse"
                class="me-2">
                <pc-bs-icon
                    name="chevron-right"
                    v-bind:rotate-on="shown" />
            </div>
            <div
                v-bind:id="internalId + '-capsule-header'"
                v-bind:class="{ 'flex-grow-1': true, 'cpadmin-text': cpAdminOnly }">
                <slot name="capsule-header">
                    {{ titleDisplay }}
                </slot>
            </div>
            <div
                v-if="showRemove"
                class="float-end">
                <img
                    src="~Images/icons/trash.svg"
                    v-bind:class="{ 'brand-remove-icon': brandTheme }"
                    class="capsule-icon"
                    v-on:click.stop="removeCapsule" />
            </div>
        </div>
        <pc-transition transition="slide">
            <div
                v-show="shown"
                v-bind:class="{ 'brand-capsule-body': brandTheme }">
                <div
                    v-if="hasTable"
                    v-bind:class="backgroundClass"
                    class="card-body capsule-body capsule-table-container">
                    <slot name="capsule-table" />
                </div>
                <div
                    v-if="hasTable && hasBody"
                    class="capsule-body-separator" />
                <div
                    v-if="hasBody"
                    v-bind:class="backgroundClass"
                    class="card-body capsule-body">
                    <slot />
                </div>
            </div>
        </pc-transition>
    </div>
</template>
<script>
    import { BP } from "Responsive/breakpoint";
    import { isAtLeast } from "Responsive/manager";
    import { safeSetItem } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pcComponentDefaults.i18n";

    export default {
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            id: { type: String, required: false, default: null },
            shown: { type: Boolean, required: true },

            title: { type: String, required: false, default: null },
            hasTable: { type: Boolean, required: false, default: false },
            hasBody: { type: Boolean, required: false, default: false },
            showRemove: { type: Boolean, required: false, default: false },
            brandTheme: { type: Boolean, required: false, default: false },
            useBackground: { type: Boolean, required: false, default: true },
            showCollapse: { type: Boolean, required: false, default: true },
            cpAdminOnly: { type: Boolean, required: false, default: false },

            saveState: { type: Boolean, required: false, default: false },
            stateKey: { type: String, required: false, default: null }
        },
        emits: ["update:shown", "remove-clicked"],
        data() {
            const _id = this.id || this.$idGenerator.getNextId("pcCapsule");
            return {
                internalId: _id,
                initialized: false
            };
        },
        computed: {
            headerClassObject() {
                return {
                    "brand-capsule-header": this.brandTheme,
                    "capsule-header-collapsable": this.showCollapse
                };
            },
            backgroundClass() {
                return !isAtLeast(this.viewport, BP.sm) && this.useBackground ? "capsule-bg-shaded" : "";
            },
            titleDisplay() {
                return this.title ?? this.pcCapsuleDefaultHeader;
            },
            ...mapI18n({ pcCapsuleDefaultHeader: KEYS.pcCapsuleDefaultHeader }, NAMESPACE)
        },
        watch: {
            shown() {
                if (this.saveState && this.stateKey) {
                    this._saveCapsuleState();
                }
            }
        },
        created() {
            this._initialize();
        },
        methods: {
            toggle() {
                if (!this.showCollapse) {
                    return;
                }
                this.$emit("update:shown", !this.shown);
            },
            removeCapsule() {
                this.$emit("remove-clicked");
            },
            async _initialize() {
                // Load saved expand/collapse state if possible
                if (this.saveState && this.stateKey) {
                    let state = localStorage.getItem(this._getLocalStorageKey());
                    state = JSON.parse(state);
                    if (state) {
                        this.$emit("update:shown", state.shown);
                        // Allow parent to update "shown" prop based on this
                        // component's saved state before we first render to
                        // avoid rendering and immediately collapsing/expanding
                        await this.$nextTick();
                    }
                }
                this.initialized = true;
            },
            _getLocalStorageKey() {
                const userId = this.$app.LoggedInUser.PublicId;
                const path = window.location.pathname + this.$app.ProgramId;
                return `pc-capsule_${this.stateKey}_${path}_${userId}`;
            },
            _saveCapsuleState() {
                if (!this.saveState || !this.stateKey) {
                    return;
                }

                const state = {
                    shown: this.shown
                };
                safeSetItem(this._getLocalStorageKey(), JSON.stringify(state));
            }
        }
    };
</script>

<style lang="scss">
    $pc-capsule-body-padding: 0.75rem;

    // Make hr within card extend all the way to its edges
    .pc-capsule-hr {
        margin: $pc-capsule-body-padding (-1 * $pc-capsule-body-padding);
    }
    .capsule-body {
        padding: $pc-capsule-body-padding;
    }
</style>

<style lang="scss" scoped>
    .capsule-header-collapsable {
        cursor: pointer;
    }

    .capsule-bg-shaded {
        background-color: var(--bs-gray-100);
    }

    .capsule-body-separator {
        height: 0;
        width: 100%;
        border-bottom: 1px solid rgba(0, 0, 0, 0.125);
    }

    .capsule-table-container .table th {
        border-top-width: 0;
    }

    .capsule-icon {
        border: 0;
        width: 1rem;
        height: 1rem;
        filter: invert(100%);
    }

    .brand-chevron {
        filter: invert(100%);
    }

    .brand-capsule-header {
        background-color: var(--bs-primary);
        color: #ffffff;
    }

    .brand-remove-icon {
        filter: none;
    }

    .brand-capsule-body {
        background-color: var(--bs-primary-100);
        opacity: 0.85;
    }

    .cpadmin-text {
        color: var(--pc-cp-admin-color);
    }
</style>
